import { useCallback, useState } from 'react';
import type { MentionUser } from '../../comments/types';
import { useSubmitColleagueFeedbackMutation } from '../api/colleagueFeedbackApi';
import { extractErrorMessage } from '../../../utils/errorUtils.ts';

type UseColleagueRatingFormOptions = {
  onSubmitted: () => void;
};

export function useColleagueRatingForm({ onSubmitted }: UseColleagueRatingFormOptions) {
  const [selectedColleague, setSelectedColleague] = useState<MentionUser | null>(null);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [feedbackText, setFeedbackText] = useState('');
  const [snackOpen, setSnackOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [submitFeedback, { isLoading: submitting }] = useSubmitColleagueFeedbackMutation();

  const handleRatingChange = useCallback((key: string, value: number | null) => {
    setRatings((prev) => ({ ...prev, [key]: value ?? 0 }));
  }, []);

  const normalizedRatings = {
    teamwork: ratings.teamwork ?? 0,
    communication: ratings.communication ?? 0,
    helpfulness: ratings.helpfulness ?? 0,
    reliability: ratings.reliability ?? 0,
  };

  const isFormValid =
    selectedColleague !== null && Object.values(normalizedRatings).every((r) => r >= 1 && r <= 5);

  const handleSubmit = async () => {
    if (!isFormValid) {
      setFormError('Please select a colleague and rate all categories (1–5).');
      return;
    }
    setFormError(null);

    try {
      await submitFeedback({
        targetId: selectedColleague!.id,
        ratings: normalizedRatings,
        feedback: feedbackText.trim(),
      }).unwrap();

      setSelectedColleague(null);
      setRatings({});
      setFeedbackText('');
      setSnackOpen(true);
      onSubmitted();
    } catch (e) {
      setFormError(extractErrorMessage(e, 'Failed to submit feedback.'));
    }
  };

  const handleReset = () => {
    setSelectedColleague(null);
    setRatings({});
    setFeedbackText('');
    setFormError(null);
  };

  return {
    selectedColleague,
    setSelectedColleague,
    ratings,
    feedbackText,
    setFeedbackText,
    formError,
    snackOpen,
    setSnackOpen,
    submitting,
    isFormValid,
    handleRatingChange,
    handleSubmit,
    handleReset,
  };
}
